import React, { useState, useCallback, useMemo, useRef } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { NormalText, PrimaryButton } from "../../../../../common/native";
import { TTemplate, TCropCoords } from "../../../../../../utils/types";
import CropRotateImage from "./CropRotateImage";
import { cropImage } from "../../../../../../utils/image";
import { isLocalEnv } from "../../../../../../utils/functions";
import { Platform } from "react-native";
import { TSideBySideProcessedImage } from "../../../../../../services/api-clinic";

type AdjustTemplateProps = {
  swapImagesModalData: {
    handleSetAfterCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords: {
        cropX: number;
        cropY: number;
        cropWidth: number;
        cropHeight: number;
      },
      selectedImage?: TSideBySideProcessedImage,
      rotationDegrees?: number,
    ) => void;
    handleSetBeforeCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords: {
        cropX: number;
        cropY: number;
        cropWidth: number;
        cropHeight: number;
      },
      selectedImage?: TSideBySideProcessedImage,
      rotationDegrees?: number,
    ) => void;
    handleSetBothCroppedImages: (
      beforeImage: string,
      afterImage: string,
      index: number,
      beforeCropCoords: TCropCoords,
      afterCropCoords: TCropCoords,
      beforeRotationDegrees?: number,
      afterRotationDegrees?: number,
    ) => void;
    currentTemplateIndex: number;
    templatesData: {
      template: TTemplate;
      afterCroppedImage: string;
      beforeCroppedImage: string;
      afterOriginalImage: string;
      beforeOriginalImage: string;
      afterDimensions: { width: number; height: number };
      beforeDimensions: { width: number; height: number };
      afterCropCoords: {
        cropX: number;
        cropY: number;
        cropWidth: number;
        cropHeight: number;
      };
      beforeCropCoords: {
        cropX: number;
        cropY: number;
        cropWidth: number;
        cropHeight: number;
      };
    }[];
    uploadedImages: { preOp: Blob[]; postOp: Blob[] };
    processedImageUrls: {
      preOp: string[];
      postOp: string[];
    };
  };
};

const AdjustTemplate: React.FC<AdjustTemplateProps> = ({
  swapImagesModalData,
}) => {
  // Split state for before and after images to prevent unnecessary re-renders
  const [beforeCropImage, setBeforeCropImage] = useState(
    swapImagesModalData.templatesData[swapImagesModalData.currentTemplateIndex]
      .beforeOriginalImage,
  );
  const [afterCropImage, setAfterCropImage] = useState(
    swapImagesModalData.templatesData[swapImagesModalData.currentTemplateIndex]
      .afterOriginalImage,
  );
  const [beforeRotationDegrees, setBeforeRotationDegrees] = useState(0);
  const [afterRotationDegrees, setAfterRotationDegrees] = useState(0);

  const currentTemplateData =
    swapImagesModalData.templatesData[swapImagesModalData.currentTemplateIndex];

  // Split crop states for before and after images
  const [beforeCropSize, setBeforeCropSize] = useState({
    width: currentTemplateData.beforeCropCoords.cropWidth,
    height: currentTemplateData.beforeCropCoords.cropHeight,
  });
  const [beforeCropPosition, setBeforeCropPosition] = useState({
    x: currentTemplateData.beforeCropCoords.cropX,
    y: currentTemplateData.beforeCropCoords.cropY,
  });

  const [afterCropSize, setAfterCropSize] = useState({
    width: currentTemplateData.afterCropCoords.cropWidth,
    height: currentTemplateData.afterCropCoords.cropHeight,
  });
  const [afterCropPosition, setAfterCropPosition] = useState({
    x: currentTemplateData.afterCropCoords.cropX,
    y: currentTemplateData.afterCropCoords.cropY,
  });

  // Use callbacks to prevent function recreation on each render
  const handleSelectBeforeImage = useCallback(
    (image: string) => setBeforeCropImage(image),
    [],
  );

  const handleSelectAfterImage = useCallback(
    (image: string) => setAfterCropImage(image),
    [],
  );

  const rotateLeft = (current: number) => (current + 270) % 360;
  const rotateRight = (current: number) => (current + 90) % 360;

  const handlePressSaveChanges = useCallback(async () => {
    const { currentTemplateIndex } = swapImagesModalData;

    const updatedBeforeCropCoords = {
      cropX: beforeCropPosition.x,
      cropY: beforeCropPosition.y,
      cropWidth: beforeCropSize.width,
      cropHeight: beforeCropSize.height,
    };

    const updatedAfterCropCoords = {
      cropX: afterCropPosition.x,
      cropY: afterCropPosition.y,
      cropWidth: afterCropSize.width,
      cropHeight: afterCropSize.height,
    };

    if (isLocalEnv) {
      console.log(
        "‼️‼️‼️ AdjustTemplateContent.tsx: Before Crop Coordinates:",
        updatedBeforeCropCoords,
      );
      console.log(
        "‼️‼️‼️ AdjustTemplateContent.tsx: After Crop Coordinates:",
        updatedAfterCropCoords,
      );
    }

    const newBeforeImage = await cropImage(
      beforeCropImage,
      updatedBeforeCropCoords.cropX,
      updatedBeforeCropCoords.cropY,
      updatedBeforeCropCoords.cropWidth,
      updatedBeforeCropCoords.cropHeight,
      beforeRotationDegrees,
    );

    const newAfterImage = await cropImage(
      afterCropImage,
      updatedAfterCropCoords.cropX,
      updatedAfterCropCoords.cropY,
      updatedAfterCropCoords.cropWidth,
      updatedAfterCropCoords.cropHeight,
      afterRotationDegrees,
    );

    // Uncomment the following line to open cropped images in new windows
    // handleDebugView(newBeforeImage, newAfterImage);

    if (swapImagesModalData.handleSetBothCroppedImages) {
      swapImagesModalData.handleSetBothCroppedImages(
        newBeforeImage,
        newAfterImage,
        currentTemplateIndex,
        updatedBeforeCropCoords,
        updatedAfterCropCoords,
        beforeRotationDegrees,
        afterRotationDegrees,
      );
    } else {
      // Fallback to the original sequential approach if needed
      // TODO: Test removing this and making the both crop coords non optional
      swapImagesModalData.handleSetBeforeCroppedImage(
        newBeforeImage,
        currentTemplateIndex,
        updatedBeforeCropCoords,
        undefined,
        beforeRotationDegrees,
      );

      swapImagesModalData.handleSetAfterCroppedImage(
        newAfterImage,
        currentTemplateIndex,
        updatedAfterCropCoords,
        undefined,
        afterRotationDegrees,
      );
    }
  }, [
    beforeCropPosition,
    beforeCropSize,
    beforeCropImage,
    beforeRotationDegrees,
    afterCropPosition,
    afterCropSize,
    afterCropImage,
    afterRotationDegrees,
    swapImagesModalData,
  ]);

  const handleDebugView = (beforeImage: string, afterImage: string) => {
    if (Platform.OS === "web") {
      const beforeWin = window.open();
      const afterWin = window.open();

      if (beforeWin) {
        beforeWin.document.write(`<img src="${beforeImage}" />`);
        beforeWin.document.title = "Before Image";
      }
      if (afterWin) {
        afterWin.document.write(`<img src="${afterImage}" />`);
        afterWin.document.title = "After Image";
      }
    }
  };

  const beforeImages = useMemo(
    () => swapImagesModalData.processedImageUrls.preOp,
    [swapImagesModalData.processedImageUrls.preOp],
  );
  const afterImages = useMemo(
    () => swapImagesModalData.processedImageUrls.postOp,
    [swapImagesModalData.processedImageUrls.postOp],
  );

  return (
    <View style={styles.container}>
      <NormalText text="Edit Image" style={styles.headerText} />

      {/* Scrollable content area */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        showsVerticalScrollIndicator={true}
      >
        <View style={styles.imagesContainer}>
          <View style={styles.componentContainer}>
            {/* Before Image */}
            <NormalText
              text={"Before Image"}
              style={{ color: "#515561", marginHorizontal: "auto" }}
            />
            <CropRotateImage
              imageUri={beforeCropImage}
              cropSize={beforeCropSize}
              setCropSize={setBeforeCropSize}
              cropPosition={beforeCropPosition}
              setCropPosition={setBeforeCropPosition}
              originalImageDimensions={currentTemplateData.beforeDimensions}
              rotationDegrees={beforeRotationDegrees}
              onRotateLeft={() =>
                setBeforeRotationDegrees((current) => rotateLeft(current))
              }
              onRotateRight={() =>
                setBeforeRotationDegrees((current) => rotateRight(current))
              }
              label="before"
            />
          </View>
          <View style={styles.componentContainer}>
            {/* After Image */}
            <NormalText
              text={"After Image"}
              style={{ color: "#515561", marginHorizontal: "auto" }}
            />
            <CropRotateImage
              imageUri={afterCropImage}
              cropSize={afterCropSize}
              setCropSize={setAfterCropSize}
              cropPosition={afterCropPosition}
              setCropPosition={setAfterCropPosition}
              originalImageDimensions={currentTemplateData.afterDimensions}
              rotationDegrees={afterRotationDegrees}
              onRotateLeft={() =>
                setAfterRotationDegrees((current) => rotateLeft(current))
              }
              onRotateRight={() =>
                setAfterRotationDegrees((current) => rotateRight(current))
              }
              label="after"
            />
          </View>
        </View>
      </ScrollView>

      <PrimaryButton
        text="Save Changes"
        width={160}
        onPress={handlePressSaveChanges}
        roundBorderRadius
        style={styles.saveButton}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  headerText: {
    marginHorizontal: "auto",
    fontSize: 14,
    paddingVertical: 6,
  },
  scrollView: {
    flex: 1,
    width: "100%",
  },
  scrollViewContent: {
    paddingVertical: 6,
  },
  imagesContainer: {
    gap: 6,
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    flexDirection: "row",
    width: "100%",
  },
  componentContainer: {
    gap: 6,
    maxWidth: 240,
    minWidth: 200,
    width: "100%",
    marginBottom: 6,
  },
  saveButton: {
    marginVertical: 10,
    alignSelf: "center",
  },
});

export default React.memo(AdjustTemplate);
